import React, { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ExportContactsUnsubscribed,
    GetContactsUnsubscribed,
    GetContactsUnsubscribedOverview,
    getUnsubscribeContactsStore,
} from '@api/store/suppressions/actions';
import { DateDropdownOptions } from '@api/store/suppressions/types';
import { AccessLevel, CompressionFormat, ContactStatus, ExportFileFormats, UnsubscribedContactsOrderBy } from '@dev/ee-api/ee-api-models';
import dayjs from '@api/services/tools/dayjs';
import { DEFAULT_ACTIVE_PAGE, DEFAULT_PER_PAGE_COUNT, LONG_DATE_FORMAT } from '@api/services/constants';
import { ContactsUnsubscribedParams, ExportContactsUnsubscribedParams } from '@api/services/ElasticApi';
import { UnsubscribeReasonsScreen } from '@dev/ee-modules';
import { isEmpty, isUndefined, toNumber } from 'lodash-es';
import { DateDropdown, Pagination, PrimaryButton, SearchInput, FiltersPopover, FilterIcon, palette } from '@dev/ee-components';
import { DateDropdownFilterInterface, ExportInterface } from './UnsubscribedInterface';
import { useDateFilterData } from '@api/services/tools/date';
import { useMobile } from '@api/services/hooks/useMobile';
import ExportModal from '@api/components/ExportModal';
import { DateOptionsInterface } from '@api/components/Statistics/Channels/SingleChannel/EmailLog/EmailLogInterface';
import { ShowDialog } from '@api/store/dialog/actions';
import { calculateOffset } from '@api/services/tools/handleUrlParams';
import { parse } from 'query-string';
import { push as Push } from 'redux-first-history';
import { SettingsPaths } from '@api/services/dictionaries/paths';
import { debounce } from '@api/services/tools/debounce';
import { usePieChartData } from './hooks/usePieChartData';
import { useTableHeaders } from './hooks/useTableHeaders';
import { useTableRowData } from './hooks/useTableRowData';
import { useLocation } from 'react-router-dom';
import { getAccountStore } from '@api/store/account/actions';
import { getDateDropdownValue } from '@api/services/tools/getDateDropdownValue';

const FiltersContainer = styled.div`
    background-color: ${palette.basic.paper};
    border-radius: 4px;
    border: 1px solid ${palette.gray[5]};
    margin-left: 8px;
    margin-right: 8px;
    padding: 11px 11px;
    width: 42px;
    height: 42px;
`;

const PaginationFC: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const { pagination, statusCount } = useSelector(getUnsubscribeContactsStore);
    const requestParams = {
        statuses: pagination.statuses,
        from: pagination.from,
        to: pagination.to,
        orderBy: pagination.orderBy,
        email: pagination.email,
    };

    const changePage = (page: number, perPage: number): void => {
        dispatch(
            GetContactsUnsubscribed({
                ...requestParams,
                offset: page * perPage - perPage,
                limit: perPage,
            })
        );
    };

    return (
        <Pagination
            dataLength={statusCount?.total}
            perPage={pagination?.perPage ?? DEFAULT_PER_PAGE_COUNT}
            activePage={pagination?.activePage ?? DEFAULT_ACTIVE_PAGE}
            handlePaginationChange={changePage}
        />
    );
};

const columnWidth: Record<string, string> = {
    email: '284px',
    status: '130px',
    dateUnsubscribed: '100px',
};

const calcFromDate = (timeRange: number): string => {
    return dayjs().subtract(timeRange, 'day').startOf('day').format(LONG_DATE_FORMAT);
};

const DateDropdownFilter: FC<DateDropdownFilterInterface> = (props: DateDropdownFilterInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { params } = props;
    const startDate = params?.from;
    const endDate = startDate && params?.to;

    const onItemClickDateRange = (ev: React.MouseEvent<HTMLElement>, { value }: Record<string, string | number>): void => {
        dispatch(
            GetContactsUnsubscribed({
                ...params,
                to: dayjs().format(LONG_DATE_FORMAT),
                from: value === DateDropdownOptions.All ? null : calcFromDate(value as number),
            })
        );
        dispatch(
            GetContactsUnsubscribedOverview(
                value === DateDropdownOptions.All ? null : calcFromDate(value as number),
                dayjs().format(LONG_DATE_FORMAT)
            )
        );
    };

    const onCalendarChange = (startDate?: string, endDate?: string): void => {
        const from = dayjs(startDate).startOf('day').format(LONG_DATE_FORMAT);
        const to = dayjs(endDate).endOf('day').format(LONG_DATE_FORMAT);

        dispatch(GetContactsUnsubscribed({ ...params, to, from }));
        dispatch(GetContactsUnsubscribedOverview(from, to));
    };

    return (
        <DateDropdown
            options={useDateFilterData()}
            defaultPlaceholder={t('last3months')}
            startDate={startDate}
            endDate={endDate}
            value={getDateDropdownValue(false, DateDropdownOptions.All)}
            type="input"
            returnRawData
            calendarOnChange={onCalendarChange}
            onItemClick={onItemClickDateRange}
            buttonVariant="tertiary"
            placement="bottom-end"
        />
    );
};

const useDateDropdownOptions = (): DateOptionsInterface[] => {
    const { t } = useTranslation();

    return [
        {
            label: t('all'),
            value: DateDropdownOptions.All,
        },
        {
            label: t('last7days'),
            value: DateDropdownOptions.Last7days,
        },
        {
            label: t('last14days'),
            value: DateDropdownOptions.Last14days,
        },
        {
            label: t('last30days'),
            value: DateDropdownOptions.Last30days,
        },
        {
            label: t('last3months'),
            value: DateDropdownOptions.Last3months,
        },
        {
            label: t('last6months'),
            value: DateDropdownOptions.Last6months,
        },
        {
            label: t('lastyear'),
            value: DateDropdownOptions.Last12Months,
        },
        {
            label: t('specificdaterange'),
            value: DateDropdownOptions.Calendar,
        },
    ];
};

const MobileFilterPopover: FC<DateDropdownFilterInterface> = (props: DateDropdownFilterInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { params } = props;

    const mobileFiltersApply = (filtersObj, momentFrom, momentTo): void => {
        const dateValue = filtersObj[0];
        let from = dayjs().subtract(dateValue, 'd').utc().hour(0).minute(0).format();
        let to = dayjs().utc();

        if (dateValue === DateDropdownOptions.Calendar) {
            from = dayjs(momentFrom).startOf('day').utc().format();
            to = dayjs(momentTo).utc();
        } else if (dateValue === DateDropdownOptions.All) {
            from = null;
        }

        dispatch(
            GetContactsUnsubscribed({
                ...params,
                to: to ? to.toJSON() : null,
                from,
            })
        );
        dispatch(GetContactsUnsubscribedOverview(from, to ? to.toJSON() : null));
    };

    const defaultValue = getDateDropdownValue(false, DateDropdownOptions.All);

    return (
        <FiltersContainer>
            <FiltersPopover
                title={t('filters')}
                onApply={(obj, momentFrom, momentTo): void => {
                    mobileFiltersApply(obj, momentFrom, momentTo);
                }}
                translations={{ apply: t('apply'), close: t('close') }}
                tabs={[
                    {
                        label: t('daterange'),
                        defaultValue: defaultValue.toString(),
                        options: useDateDropdownOptions(),
                        type: 'date',
                        startDate: params?.from,
                        endDate: params?.to,
                    },
                ]}
            >
                <FilterIcon color="#718096" height={20} width={20} />
            </FiltersPopover>
        </FiltersContainer>
    );
};

const Export: FC<ExportInterface> = (props: ExportInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const exportCompressionFormat = useRef(CompressionFormat.Zip);
    const exportFileFormat = useRef(ExportFileFormats.Csv);
    let fileNameActivityRef: HTMLInputElement;

    const { pagination } = useSelector(getUnsubscribeContactsStore);

    const showExportDialog = (): void => {
        dispatch(
            ShowDialog({
                title: t('export'),
                body: (
                    <ExportModal
                        onCompressionChange={(enabled: boolean): void => {
                            exportCompressionFormat.current = enabled ? CompressionFormat.Zip : CompressionFormat.None;
                        }}
                        onFileFormatChange={(data: { value: ExportFileFormats; label: string }): void => {
                            exportFileFormat.current = data.value;
                        }}
                        inputRef={(element) => {
                            fileNameActivityRef = element;
                        }}
                    />
                ),
                confirmLabel: t('export'),
                cancelLabel: t('cancel'),
                onClickConfirm: (): void => {
                    const exportUnsubscribeRequestParams: ExportContactsUnsubscribedParams = {
                        statuses: pagination.statuses,
                        from: pagination.from,
                        to: pagination.to,
                        orderBy: pagination.orderBy,
                        email: pagination.email,
                        fileFormat: exportFileFormat.current,
                        compressionFormat: exportCompressionFormat.current,
                        fileName: fileNameActivityRef?.value || 'export',
                    };
                    dispatch(ExportContactsUnsubscribed(exportUnsubscribeRequestParams));
                },
            })
        );
    };

    return <PrimaryButton onClick={showExportDialog} disabled={props?.isDisabled} text={t('export')} />;
};

const SearchInputFC: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pagination } = useSelector(getUnsubscribeContactsStore);
    const offset = calculateOffset(pagination.activePage, pagination.perPage);
    const location = useLocation();

    const query = parse(location.search);
    const email = query?.email as string;

    const requestParams: ContactsUnsubscribedParams = {
        limit: pagination.perPage,
        offset,
        statuses: pagination.statuses,
        from: pagination.from,
        to: pagination.to,
        orderBy: pagination.orderBy,
        email,
    };

    const onChangeSearchInputHandle = (event?: any): void => {
        const searchString = event?.target?.value;

        debounce((): void => {
            dispatch(
                GetContactsUnsubscribed({
                    ...requestParams,
                    email: isUndefined(searchString) ? '' : searchString,
                })
            );
        }, 500);
    };

    return (
        <SearchInput
            id="searchUnsubscribed"
            name="search"
            placeholder={t('searchforcontacts')}
            type="text"
            onChange={onChangeSearchInputHandle}
            onDelete={onChangeSearchInputHandle}
            defaultValue={email}
        />
    );
};

const Unsubscribed: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const location = useLocation();
    const query = parse(location.search);
    const { GeneralInfo } = useSelector(getAccountStore);
    const isMobile = useMobile();

    const { pagination, overview, loadingUnsubscribedOverview, loadingUnsubscribed, unsubReasons } =
        useSelector(getUnsubscribeContactsStore);

    const to = !query?.to ? pagination?.to : (query?.to as string);
    const from = !query?.from ? pagination?.from : (query?.from as string);
    const perPageParam = !query?.perPage ? pagination?.perPage : toNumber(query?.perPage);
    const activePageParam = !query?.activePage ? pagination?.activePage : toNumber(query?.activePage);
    const offset = calculateOffset(activePageParam, perPageParam);
    const statuses = !query?.statuses ? pagination?.statuses : (query?.statuses as ContactStatus[]);
    const email = !query?.email ? pagination?.email : (query?.email as string);
    const orderBy = !query?.orderBy ? pagination?.orderBy : (query?.orderBy as UnsubscribedContactsOrderBy);

    const requestParams: ContactsUnsubscribedParams = {
        limit: perPageParam,
        offset,
        statuses,
        from,
        to,
        email,
        orderBy,
    };

    useEffect(() => {
        dispatch(GetContactsUnsubscribedOverview(from, to));
        dispatch(GetContactsUnsubscribed(requestParams));
    }, []);

    const pieChartData = usePieChartData();
    const tableHeaders = useTableHeaders(requestParams);
    const row = useTableRowData();
    const canSeeExportButton = GeneralInfo?.AccountAccessLevel?.includes?.(AccessLevel.Export);
    const mobileTableHeaders = [t('email'), t('status'), t('date'), t('how'), t('reason'), t('feedback')];

    return (
        <UnsubscribeReasonsScreen
            pieChartData={pieChartData}
            filter={isMobile ? <MobileFilterPopover params={requestParams} /> : <DateDropdownFilter params={requestParams} />}
            exportButton={canSeeExportButton && <Export isDisabled={!loadingUnsubscribed && isEmpty(row)} />}
            reasons={unsubReasons || []}
            handleManageUnsubscribeClick={(): void => {
                dispatch(Push(SettingsPaths.Unsubscribe));
            }}
            loadingOverview={loadingUnsubscribedOverview}
            loadingUnsubscribed={loadingUnsubscribed}
            tableHeaders={tableHeaders}
            mobileTableHeaders={mobileTableHeaders}
            columnsWidth={columnWidth}
            tableRows={row}
            searchInput={<SearchInputFC />}
            isEmptyTable={!loadingUnsubscribed && isEmpty(row)}
            emptyStateTable={{ text: t('emptystate') }}
            pagination={<PaginationFC />}
            isEmptyChart={!loadingUnsubscribedOverview && !overview?.TotalUnsubscribed}
            isMobile={isMobile}
        />
    );
};

export default Unsubscribed;
